import { create } from 'zustand'
import { persist, subscribeWithSelector } from 'zustand/middleware'
import type { ItemCard, ItemPriority, ItemStatus } from '@/types/dashboard'
import { supabase } from '@/lib/supabase'

interface ItemsBoardState {
  items: ItemCard[]
  addItem: (title: string, priority: ItemPriority | ItemPriority[], status: ItemStatus, description?: string) => void
  toggleComplete: (id: string) => void
  removeItem: (id: string) => void
  reorderItem: (id: string, newOrder: number) => void
  moveToStatus: (id: string, newStatus: ItemStatus) => void
}

// Debounced sync to Supabase
let syncTimeout: NodeJS.Timeout | null = null
const debouncedSyncGoals = (items: ItemCard[]) => {
  if (syncTimeout) clearTimeout(syncTimeout)

  syncTimeout = setTimeout(async () => {
    try {
      // Map items to DB format
      const goalsToUpsert = items.map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        // Serialize array priorities as JSON
        priority: Array.isArray(item.priority)
          ? JSON.stringify(item.priority)
          : item.priority,
        status: item.status,
        completed: item.completed,
        order_num: item.order,
        created_at: item.createdAt,
        completed_at: item.completedAt,
      }))

      await supabase
        .from('office_tv_goals')
        .upsert(goalsToUpsert, { onConflict: 'id' })
    } catch (error) {
      console.error('Error syncing goals:', error)
    }
  }, 1000) // 1 second debounce
}

export const useItemsBoardStore = create<ItemsBoardState>()(
  subscribeWithSelector(
    persist(
      (set) => ({
      items: [
        // Last week - completed
        {
          id: crypto.randomUUID(),
          title: 'Fix hover and ship to Skycutter',
          priority: ['money', 'critical'],
          status: 'last-week',
          completed: true,
          order: 1,
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          completedAt: new Date().toISOString(),
        },
        // Current week goals
        {
          id: crypto.randomUUID(),
          title: 'Sales calls & demos',
          priority: 'money',
          status: 'current',
          completed: false,
          order: 1,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'Working with SOCOM',
          description: 'Spend that 2.5m !!',
          priority: 'money',
          status: 'current',
          completed: false,
          order: 2,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'Get units to Buntar',
          priority: 'money',
          status: 'current',
          completed: false,
          order: 3,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'Test Cyclops w/o GPS',
          priority: 'critical',
          status: 'current',
          completed: false,
          order: 4,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'Fix new user flow for Vozilla',
          priority: 'growth',
          status: 'current',
          completed: false,
          order: 5,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'Fix Vozilla bugs',
          priority: 'critical',
          status: 'current',
          completed: false,
          order: 6,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'Test Camera calibration works on live flight',
          priority: 'critical',
          status: 'current',
          completed: false,
          order: 7,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'PX4',
          priority: 'growth',
          status: 'current',
          completed: false,
          order: 8,
          createdAt: new Date().toISOString(),
        },
        {
          id: crypto.randomUUID(),
          title: 'ARK Electronics board',
          priority: 'growth',
          status: 'current',
          completed: false,
          order: 9,
          createdAt: new Date().toISOString(),
        },
        // Backlog
        {
          id: crypto.randomUUID(),
          title: 'Customer interviews',
          priority: 'backlog',
          status: 'future',
          completed: false,
          order: 1,
          createdAt: new Date().toISOString(),
        },
      ],

      addItem: (title: string, priority: ItemPriority | ItemPriority[], status: ItemStatus, description?: string) =>
        set((state) => {
          const maxOrder = Math.max(0, ...state.items.filter((i) => i.status === status).map((i) => i.order))
          return {
            items: [
              ...state.items,
              {
                id: crypto.randomUUID(),
                title,
                description,
                priority,
                status,
                completed: false,
                order: maxOrder + 1,
                createdAt: new Date().toISOString(),
              },
            ],
          }
        }),

      toggleComplete: (id: string) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id
              ? {
                  ...item,
                  completed: !item.completed,
                  completedAt: !item.completed ? new Date().toISOString() : undefined,
                }
              : item
          ),
        })),

      removeItem: (id: string) =>
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        })),

      reorderItem: (id: string, newOrder: number) =>
        set((state) => ({
          items: state.items.map((item) => (item.id === id ? { ...item, order: newOrder } : item)),
        })),

      moveToStatus: (id: string, newStatus: ItemStatus) =>
        set((state) => {
          const maxOrder = Math.max(0, ...state.items.filter((i) => i.status === newStatus).map((i) => i.order))
          return {
            items: state.items.map((item) =>
              item.id === id
                ? {
                    ...item,
                    status: newStatus,
                    order: maxOrder + 1,
                    completed: newStatus === 'last-week' ? item.completed : false,
                  }
                : item
            ),
          }
        }),
      }),
      {
        name: 'items-board-store',
      }
    )
  )
)

// Subscribe to items changes and sync to Supabase
useItemsBoardStore.subscribe(
  (state) => state.items,
  (items) => {
    debouncedSyncGoals(items)
  }
)
